import React, {
    type FC,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from 'react';
import { ChakraProvider } from '@chakra-ui/provider';
import {
    Box,
    Toast as ChakraToast,
    SlideFade,
    theme as chakraTheme,
    extendTheme
} from '@chakra-ui/react';
import { type Root, createRoot } from 'react-dom/client';
import { getGalleryTheme } from './theme';

/** Mount target: `#sp-client-gallery-toast-host` in client gallery `default.phtml`. */
export const CLIENT_GALLERY_TOAST_HOST_ID = 'sp-client-gallery-toast-host';

const fallbackTheme = extendTheme({}) as unknown as Record<string, unknown>;

/** Chakra's default `toastSpacing`, which its toast manager applies to each toast's wrapper. */
const TOAST_SPACING = '0.5rem';

/** Chakra's default toast `duration`. */
const TOAST_DURATION = 5000;

/** The title weight the gallery's own toasts use. */
const TOAST_TITLE_FONT_WEIGHT = 'semibold';

/**
 * The enter and exit timings Chakra's toast manager animates with. Its wrapper component is not
 * exported, so the host drives Chakra's own SlideFade with the same values.
 */
const TOAST_TRANSITION = {
    enter: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
    exit: { duration: 0.2, ease: [0.4, 0, 1, 1] }
} as const;

/** How long to leave a toast mounted for its exit transition before dropping it. */
const TOAST_EXIT_MS = TOAST_TRANSITION.exit.duration * 1000;

type ActiveToast = {
    id: number;
    node: React.ReactNode;
    theme: Record<string, unknown>;
};

let reactRoot: Root | null = null;
let nextToastId = 0;

type GalleryToastBridge = {
    dismissAll: () => void;
    show: (theme: Record<string, unknown>, node: React.ReactNode) => void;
};

let galleryToastBridge: GalleryToastBridge | null = null;

/** Toasts raised before the host's bridge registered, shown as soon as it does. */
let pendingToasts: { node: React.ReactNode; theme: Record<string, unknown> }[] = [];

/** Dismisses the toast it is rendered in, so a toast's close button only closes that one. */
const DismissToastContext = createContext<() => void>(() => undefined);

/** For a toast's own close button - `onClose={useDismissGalleryToast()}`. */
export const useDismissGalleryToast = () => useContext(DismissToastContext);

type GalleryToastProps = { onDone: (id: number) => void; toast: ActiveToast };

/** One toast in the stack: its own lifetime, hover pause, theme, and dismissal. */
const GalleryToast: FC<GalleryToastProps> = ({ onDone, toast }) => {
    const [isVisible, setIsVisible] = useState(true);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimer = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    // Chakra restarts the full duration when the pointer leaves, rather than resuming.
    const startTimer = useCallback(() => {
        clearTimer();

        timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            setIsVisible(false);
        }, TOAST_DURATION);
    }, [clearTimer]);

    const dismiss = useCallback(() => {
        clearTimer();
        setIsVisible(false);
    }, [clearTimer]);

    useEffect(() => {
        startTimer();

        return clearTimer;
    }, [clearTimer, startTimer]);

    // Drop it from the stack once its exit transition has had time to play, so the toasts below
    // it close the gap rather than leaving one behind.
    useEffect(() => {
        if (isVisible) {
            return undefined;
        }

        const removal = setTimeout(() => onDone(toast.id), TOAST_EXIT_MS);

        return () => clearTimeout(removal);
    }, [isVisible, onDone, toast.id]);

    return (
        <ChakraProvider resetCSS={false} theme={toast.theme}>
            <SlideFade
                in={isVisible}
                offsetY="24px"
                unmountOnExit
                transition={TOAST_TRANSITION}
                onMouseEnter={clearTimer}
                onMouseLeave={startTimer}
            >
                <DismissToastContext.Provider value={dismiss}>
                    {toast.node}
                </DismissToastContext.Provider>
            </SlideFade>
        </ChakraProvider>
    );
};

/**
 * Imperative bridge for gallery React islands: multiple react2angular roots cannot share
 * Chakra ToastProvider, while Chakra’s toast store is a singleton. This host renders toasts
 * in one tree so they are not duplicated.
 */
const GalleryToastHost: FC = () => {
    const [toasts, setToasts] = useState<ActiveToast[]>([]);

    const removeToast = useCallback((id: number) => {
        setToasts((current) => current.filter((toast) => toast.id !== id));
    }, []);

    useLayoutEffect(() => {
        const show = (theme: Record<string, unknown>, node: React.ReactNode) => {
            // Appended, so the newest sits nearest the bottom edge and pushes the rest up, which
            // is the order Chakra's toast manager kept for bottom-positioned toasts.
            setToasts((current) => [...current, { id: nextToastId++, node, theme }]);
        };

        galleryToastBridge = { dismissAll: () => setToasts([]), show };

        if (pendingToasts.length) {
            const held = pendingToasts;

            pendingToasts = [];
            held.forEach(({ node, theme }) => show(theme, node));
        }

        return () => {
            galleryToastBridge = null;
        };
    }, []);

    return (
        <ChakraProvider resetCSS={false} theme={fallbackTheme}>
            <Box
                role="status"
                aria-live="polite"
                position="fixed"
                bottom="24px"
                left="50%"
                transform="translateX(-50%)"
                zIndex={5500}
                pointerEvents="auto"
                display="flex"
                flexDirection="column"
                alignItems="center"
            >
                {toasts.map((toast) => (
                    <GalleryToast key={toast.id} onDone={removeToast} toast={toast} />
                ))}
            </Box>
        </ChakraProvider>
    );
};

/** Called once from the client gallery Angular app `run` block after the host element exists in the DOM. */
export function initClientGalleryToastHost(): void {
    if (typeof document === 'undefined') {
        return;
    }

    if (reactRoot) {
        return;
    }

    const el = document.getElementById(CLIENT_GALLERY_TOAST_HOST_ID);

    if (!el) {
        return;
    }

    reactRoot = createRoot(el);
    reactRoot.render(<GalleryToastHost />);
}

/** Unmount the toast root (e.g. Jest teardown). */
export function disposeClientGalleryToastHost(): void {
    if (reactRoot) {
        reactRoot.unmount();
        reactRoot = null;
    }

    pendingToasts = [];
}

export function showGalleryToast(theme: Record<string, unknown>, node: React.ReactNode): void {
    if (galleryToastBridge) {
        galleryToastBridge.show(theme, node);

        return;
    }

    // The bridge registers in a layout effect, so the host is not ready the moment it is
    // initialized. Hold the toast rather than dropping it - GooglePhotosExportController raises
    // one during route setup when the authKey is missing.
    pendingToasts = [...pendingToasts, { node, theme }];
}

/** Clears the whole stack. A toast's own close button uses `useDismissGalleryToast` instead. */
export function dismissGalleryToast(): void {
    galleryToastBridge?.dismissAll();
}

/**
 * The gallery theme, with Chakra's stock Alert styling back in place.
 *
 * These toasts used to render inside the ToastProviders that sp-ui's withCommon mounts, under
 * that package's theme, so they took Chakra's Alert defaults. The gallery theme restyles Alert
 * for the in-page alerts it was written for - a normal-weight title and its own font stack -
 * which is not how these toasts looked. Replacing that one component config leaves the rest of
 * the gallery theme, brand colors included, applying as usual.
 *
 * The title weight is the one exception: Chakra's stock alert title is bolder than the gallery's
 * own favorite/label/hide toasts, which this host also renders, so it matches those instead.
 */
function getToastTheme(): Record<string, unknown> {
    const base = (getGalleryTheme() ?? fallbackTheme) as {
        components?: Record<string, unknown>;
    } & Record<string, unknown>;
    const { baseStyle, ...alert } = chakraTheme.components.Alert;
    const { baseStyle: closeButtonBaseStyle, ...closeButton } = chakraTheme.components.CloseButton;

    return {
        ...base,
        components: {
            ...base.components,
            Alert: {
                ...alert,
                baseStyle: {
                    ...baseStyle,
                    // Matches ts/common/components/Toast, the gallery's own toast.
                    title: { ...baseStyle?.title, fontWeight: TOAST_TITLE_FONT_WEIGHT }
                }
            },
            CloseButton: {
                ...closeButton,
                // A button does not inherit its parent's color on its own, and this host mounts
                // its provider with resetCSS off, so Chakra's `button { color: inherit }` is not
                // there to do it. Without this the close button falls back to the browser's own
                // button color rather than the alert's text color.
                baseStyle: { ...closeButtonBaseStyle, color: 'inherit' }
            }
        }
    };
}

export type GalleryStatusToastOptions = {
    description?: string;
    isClosable?: boolean;
    status?: 'error' | 'info' | 'success' | 'warning';
    title: string;
};

/** Chakra's own toast component, sized as its manager sizes each toast. */
const GalleryStatusToast: FC<GalleryStatusToastOptions> = (options) => {
    const dismiss = useDismissGalleryToast();

    return (
        <Box
            className="chakra-toast__inner"
            margin={TOAST_SPACING}
            pointerEvents="auto"
            minWidth="300px"
            maxWidth="560px"
        >
            <ChakraToast {...options} onClose={dismiss} />
        </Box>
    );
};

/**
 * Shows a status toast through this host.
 *
 * Angular's spToast raised these through Chakra's standalone toast, whose store is a singleton
 * that every mounted ToastProvider renders - so one call produced a stack of identical copies,
 * and any one of those providers unmounting (such as a modal closing) dismissed them all.
 * Rendering here puts the toast in a single tree that outlives the modal that triggered it.
 *
 * @param options Title, optional description, status, and whether a close button is shown
 * @returns void
 */
export function showGalleryStatusToast(options: GalleryStatusToastOptions): void {
    showGalleryToast(getToastTheme(), <GalleryStatusToast {...options} />);
}
